import type { ComponentChildren } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'wouter-preact'
import { getMe, listConversations } from '../api/endpoints'
import { LogoMark } from './ui'
import { DEMO_SITE, demoSite, isDemoMode } from '../utils/demo-mode'
import { isDemoViewer } from '../auth/session'

interface NavItem {
  href: string
  label: string
  adminOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { href: '/conversations', label: 'Inbox' },
  { href: '/playbook', label: 'Playbook' },
  { href: '/knowledge', label: 'Knowledge' },
  { href: '/quality', label: 'Quality', adminOnly: true },
  { href: '/tune', label: 'Tune', adminOnly: true },
  { href: '/sites', label: 'Sites', adminOnly: true },
  { href: '/settings', label: 'Settings' },
]

export function AppShell({ children }: { children: ComponentChildren }) {
  const [location] = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Filter admin-only items out for non-admins. Cached query — same /me
  // call other pages already make, so this is essentially free.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe })
  const isAdmin = meQuery.data?.operator?.isAdmin === true

  // Live needs-reply chip — cheap (limit 1; the counts ride the same
  // response). Hidden at zero: the chip is an alarm, not a status lamp.
  const chipQuery = useQuery({
    queryKey: ['needs-reply-chip'],
    queryFn: ({ signal }) => listConversations({ signal, status: 'active', limit: 1 }),
    refetchInterval: () =>
      typeof document !== 'undefined' && document.visibilityState === 'hidden' ? false : 30_000,
  })
  const needsReply = chipQuery.data?.counts?.needsReply ?? 0
  const demoViewer = isDemoViewer()
  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.adminOnly || isAdmin || (demoViewer && (item.href === '/quality' || item.href === '/tune'))
  )

  const isActive = (path: string) =>
    location === path || location.startsWith(`${path}/`)

  // Close drawer on route change so tapping a link doesn't leave it open
  useEffect(() => {
    setDrawerOpen(false)
  }, [location])

  // Close on ESC
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div class="app-shell">
      <header class="nav">
        <Link href="/conversations" class="nav-brand">
          <span class="nav-mark">
            <LogoMark size={22} />
          </span>
          <span class="nav-brand-text">Monty</span>
          <span class="nav-byline">by Momentum</span>
          {isDemoMode() && (
            // Scoped to a real tenant (?demoSite=<id>): show the site id so
            // the pill reads as a tenant indicator on camera, not "DEMO".
            <span
              title="Demo mode is on — other sites are hidden on this device (Settings → Demo mode, or ?demoSite=off)"
              style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '.06em', color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 999, padding: '2px 8px', textTransform: 'uppercase' }}
            >
              {demoSite() === DEMO_SITE ? 'DEMO' : demoSite()}
            </span>
          )}
        </Link>

        {needsReply > 0 && (
          <Link href="/conversations" class="nav-needs-chip" title="Conversations waiting for a reply">
            <span class="nav-needs-pulse" />
            {needsReply} need{needsReply === 1 ? 's' : ''} reply
          </Link>
        )}

        {/* Wide-viewport horizontal nav */}
        <nav class="nav-actions">
          {visibleItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              class={`nav-link ${isActive(item.href) ? 'active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Narrow-viewport burger button */}
        <button
          type="button"
          class="nav-burger"
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={drawerOpen}
          aria-controls="nav-drawer"
          onClick={() => setDrawerOpen((v) => !v)}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.4"
            stroke-linecap="round"
          >
            {drawerOpen ? (
              <>
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </>
            ) : (
              <>
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </>
            )}
          </svg>
        </button>
      </header>

      {/* Mobile drawer overlay + panel */}
      {drawerOpen && (
        <>
          <div
            class="nav-drawer-scrim"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <nav id="nav-drawer" class="nav-drawer" aria-label="Main">
            {visibleItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                class={`nav-drawer-link ${isActive(item.href) ? 'active' : ''}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </>
      )}

      {demoViewer && (
        <div class="demo-strip" role="note">
          <strong>Read-only demo.</strong> Spoke &amp; Sprocket Cycles is a fictional
          shop. Explore the inbox, quality scores and Tune; replying and saving
          changes are switched off.
        </div>
      )}

      <main class="container">{children}</main>
    </div>
  )
}
